/**
 * AetherLite Admin endpoints (requieren is_admin=true).
 *
 * Backend prefix: /aetherlite/admin/*
 * Implementado en aethercoach-mvp/backend/routers/aetherlite_admin.py
 *
 * NOTA: las shapes aquí siguen al pie de la letra al backend real.
 * Los endpoints list_* devuelven arrays directos (no paginated wrappers).
 */
import { apiBaseUrl, apiFetch, getToken } from "./client";

// ---- Dashboard ----

export interface AdminDashboardKpis {
  users_total: number;
  users_by_tier: Record<string, number>;
  users_active_30d: number;
  plans_total: number;
  plans_created_30d: number;
  shares_total: number;
  shares_views_total: number;
  payments_total_count: number;
  payments_total_cop: number;     // suma de pagos último mes (rough MRR)
  mrr_active_cop: number;         // estimación: # users * precio del tier
  conversions_free_to_solo: number;
  // Bag por compatibilidad (campos opcionales adicionales).
  [k: string]: any;
}

export async function dashboard(): Promise<AdminDashboardKpis> {
  return apiFetch<AdminDashboardKpis>("/aetherlite/admin/dashboard");
}

// ---- Users ----

/** Resumen del coach (lo que devuelve el listado y PATCH). */
export interface AdminUserSummary {
  id: number;
  email: string;
  name: string | null;
  plan_tier: string;
  is_active: boolean;
  is_admin: boolean;
  plans_count: number;
  last_payment_at: string | null;  // ISO datetime
  created_at: string;
  updated_at: string;
}

/** Detalle del coach con datos extra + lista de pagos. */
export interface AdminUserDetail extends AdminUserSummary {
  picture_url: string | null;
  instagram: string | null;
  web: string | null;
  phone: string | null;
  payments: AdminPayment[];
}

export interface ListUsersFilters {
  q?: string;
  tier?: string;
  active_only?: boolean;
  limit?: number;
  offset?: number;
}

export async function listUsers(filters: ListUsersFilters = {}): Promise<AdminUserSummary[]> {
  const params = new URLSearchParams();
  if (filters.q) params.set("q", filters.q);
  if (filters.tier) params.set("tier", filters.tier);
  if (filters.active_only) params.set("active_only", "true");
  if (filters.limit !== undefined) params.set("limit", String(filters.limit));
  if (filters.offset !== undefined) params.set("offset", String(filters.offset));
  const qs = params.toString();
  return apiFetch<AdminUserSummary[]>(`/aetherlite/admin/users${qs ? `?${qs}` : ""}`);
}

export async function getUser(id: number): Promise<AdminUserDetail> {
  return apiFetch<AdminUserDetail>(`/aetherlite/admin/users/${id}`);
}

export interface PatchUserPayload {
  plan_tier?: string;
  is_active?: boolean;
  is_admin?: boolean;
}

export async function patchUser(id: number, data: PatchUserPayload): Promise<AdminUserSummary> {
  return apiFetch<AdminUserSummary>(`/aetherlite/admin/users/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

// ---- Payments ----

export interface AdminPayment {
  id: number;
  user_id: number;
  user_email: string | null;
  amount_cop: number;
  currency: string;
  plan_tier: string;
  period_start: string; // ISO date
  period_end: string;   // ISO date
  method: string | null;
  reference: string | null;
  notes: string | null;
  created_at: string;   // ISO datetime
}

export interface ListPaymentsFilter {
  user_id?: number;
  limit?: number;
  offset?: number;
}

export async function listPayments(filter: ListPaymentsFilter = {}): Promise<AdminPayment[]> {
  const params = new URLSearchParams();
  if (filter.user_id) params.set("user_id", String(filter.user_id));
  if (filter.limit !== undefined) params.set("limit", String(filter.limit));
  if (filter.offset !== undefined) params.set("offset", String(filter.offset));
  const qs = params.toString();
  return apiFetch<AdminPayment[]>(`/aetherlite/admin/payments${qs ? `?${qs}` : ""}`);
}

export interface CreatePaymentPayload {
  user_id: number;
  amount_cop: number;
  plan_tier: string;
  period_start: string;            // ISO date "YYYY-MM-DD"
  period_end?: string | null;      // opcional, default backend = +30d
  method?: string | null;
  reference?: string | null;
  notes?: string | null;
}

export async function createPayment(data: CreatePaymentPayload): Promise<AdminPayment> {
  return apiFetch<AdminPayment>("/aetherlite/admin/payments", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function deletePayment(id: number): Promise<void> {
  return apiFetch<void>(`/aetherlite/admin/payments/${id}`, { method: "DELETE" });
}

// ---- CSV exports ----
// Devuelven el CSV crudo (text/csv). El caller dispara el download.
// El backend solo expone users / payments / plans (no shares).

export type CsvKind = "users" | "payments" | "plans" | "ai-usage";

export async function exportCsv(kind: CsvKind): Promise<Blob> {
  const token = getToken();
  const res = await fetch(`${apiBaseUrl()}/aetherlite/admin/export/${kind}.csv`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) throw new Error(`Export ${kind} failed: HTTP ${res.status}`);
  return res.blob();
}

// ---- AI Usage observability (Fase 4) ----

export interface AiUsageStatusBreakdown {
  success: number;
  error: number;
  timeout: number;
  malformed: number;
  quota_exceeded: number;
}

export interface AiUsageTopCoach {
  user_id: number;
  email: string;
  name: string | null;
  plan_tier: string;
  success_count: number;
  error_count: number;
  total_cost_usd: number;
  last_at: string | null;
}

export interface AiUsageSummary {
  month_label: string;
  total_generations: number;
  successful_generations: number;
  total_cost_usd: number;
  avg_cost_per_success_usd: number;
  total_tokens_in: number;
  total_tokens_out: number;
  total_tokens_cache_read: number;
  unique_coaches: number;
  status_breakdown: AiUsageStatusBreakdown;
  top_coaches: AiUsageTopCoach[];
}

export interface AiUsageRow {
  id: number;
  user_id: number;
  user_email: string;
  user_name: string | null;
  plan_id: number | null;
  kind: string;
  model: string;
  status: string;
  tokens_in: number | null;
  tokens_out: number | null;
  tokens_cache_read: number | null;
  cost_usd: number | null;
  duration_ms: number | null;
  prompt_summary: string | null;
  error: string | null;
  created_at: string;
}

export interface AiUsageListParams {
  limit?: number;
  offset?: number;
  status?: string;
  user_id?: number;
}

export async function adminAiUsageSummary(): Promise<AiUsageSummary> {
  return apiFetch<AiUsageSummary>("/aetherlite/admin/ai-usage/summary");
}

export async function adminAiUsageList(params: AiUsageListParams = {}): Promise<AiUsageRow[]> {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.offset !== undefined) qs.set("offset", String(params.offset));
  if (params.status) qs.set("status", params.status);
  if (params.user_id !== undefined) qs.set("user_id", String(params.user_id));
  const q = qs.toString();
  return apiFetch<AiUsageRow[]>(`/aetherlite/admin/ai-usage/list${q ? `?${q}` : ""}`);
}

export interface AiConfig {
  client_monthly_limit: number;
}

export async function adminGetAiConfig(): Promise<AiConfig> {
  return apiFetch<AiConfig>("/aetherlite/admin/ai-config");
}

export async function adminSetAiConfig(clientMonthlyLimit: number): Promise<AiConfig> {
  return apiFetch<AiConfig>("/aetherlite/admin/ai-config", {
    method: "PUT",
    body: JSON.stringify({ client_monthly_limit: clientMonthlyLimit }),
  });
}

/** Helper UI para disparar la descarga del blob al filesystem. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---- Catálogo global de alimentos (food_library) ----

export interface AdminFood {
  id: number;
  name: string;
  base_unit: string;
  base_quantity: number;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  category: string | null;
  data_source: string | null;
}

export interface AdminFoodInput {
  name: string;
  base_unit?: string;
  base_quantity?: number;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  category?: string | null;
  data_source?: string | null;
}

export async function adminListFoods(params: { query?: string; category?: string } = {}): Promise<AdminFood[]> {
  const qs = new URLSearchParams();
  if (params.query) qs.set("query", params.query);
  if (params.category) qs.set("category", params.category);
  const q = qs.toString();
  return apiFetch<AdminFood[]>(`/aetherlite/admin/foods${q ? `?${q}` : ""}`);
}

export async function adminCreateFood(payload: AdminFoodInput): Promise<AdminFood> {
  return apiFetch<AdminFood>("/aetherlite/admin/foods", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function adminUpdateFood(id: number, patch: Partial<AdminFoodInput>): Promise<AdminFood> {
  return apiFetch<AdminFood>(`/aetherlite/admin/foods/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export async function adminDeleteFood(id: number): Promise<void> {
  return apiFetch<void>(`/aetherlite/admin/foods/${id}`, { method: "DELETE" });
}

// ---- Catálogo global de ejercicios (exercise_library) — biblioteca completa ----

export interface AdminExercise {
  id: number;
  name: string;
  name_en?: string | null;
  muscle: string;
  equipment?: string | null;
  video_url?: string | null;
  instructions?: string | null;
  secondary_muscles?: string | null;
  difficulty?: string | null;
  category?: string | null;
}

export interface AdminExerciseInput {
  name: string;
  name_en?: string | null;
  muscle: string;
  equipment?: string | null;
  video_url?: string | null;
  instructions?: string | null;
  secondary_muscles?: string | null;
  difficulty?: string | null;
  category?: string | null;
}

export async function adminListExercises(params: {
  query?: string;
  muscle?: string;
  has_gif?: boolean;
} = {}): Promise<AdminExercise[]> {
  const qs = new URLSearchParams();
  if (params.query) qs.set("query", params.query);
  if (params.muscle) qs.set("muscle", params.muscle);
  if (typeof params.has_gif === "boolean") qs.set("has_gif", String(params.has_gif));
  const q = qs.toString();
  return apiFetch<AdminExercise[]>(
    `/aetherlite/admin/exercises${q ? `?${q}` : ""}`,
  );
}

/** Un ejercicio usado en planes que no resolvió contra el catálogo (sin libId
 *  ni gifUrl). Alerta para sumarlo al catálogo con su GIF. */
export interface MissingGifExercise {
  name: string;
  plan_count: number;
  coach_count: number;
  coaches: string[];
}

export async function adminExercisesMissingGifs(): Promise<MissingGifExercise[]> {
  return apiFetch<MissingGifExercise[]>("/aetherlite/admin/exercises/missing-gifs");
}

export async function adminCreateExercise(
  payload: AdminExerciseInput,
): Promise<AdminExercise> {
  return apiFetch<AdminExercise>("/aetherlite/admin/exercises", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function adminUpdateExercise(
  id: number,
  patch: Partial<AdminExerciseInput>,
): Promise<AdminExercise> {
  return apiFetch<AdminExercise>(`/aetherlite/admin/exercises/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export async function adminDeleteExercise(id: number): Promise<void> {
  return apiFetch<void>(`/aetherlite/admin/exercises/${id}`, { method: "DELETE" });
}

export async function adminListMuscles(): Promise<string[]> {
  return apiFetch<string[]>("/aetherlite/admin/exercises/muscles");
}

/** Sube un archivo GIF/imagen/video al servidor — devuelve la URL pública. */
export async function adminUploadExerciseGif(
  file: File,
): Promise<{ url: string; file_size_kb: number; content_type: string }> {
  const token = getToken();
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(
    `${apiBaseUrl()}/aetherlite/admin/exercises/upload-gif`,
    {
      method: "POST",
      body: fd,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    },
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Upload falló (HTTP ${res.status}): ${text}`);
  }
  return res.json();
}

/** Descarga un GIF desde una URL pública en internet y lo guarda en nuestro
 *  servidor — devuelve la URL local. Útil para no depender del CDN externo. */
export async function adminFetchExerciseGif(
  url: string,
): Promise<{ url: string; file_size_kb: number }> {
  return apiFetch<{ url: string; file_size_kb: number }>(
    "/aetherlite/admin/exercises/fetch-gif",
    {
      method: "POST",
      body: JSON.stringify({ url }),
    },
  );
}

// ============================================================================
// Admin accounts (gestionar otros admins — solo super-admin)
// ============================================================================

export interface AdminAccount {
  id: number;
  email: string;
  name: string | null;
  is_active: boolean;
  is_super: boolean;             // convención: admin de menor id es super
  has_password: boolean;         // tiene login email/pwd
  has_google: boolean;           // tiene login Google
  created_at: string;            // ISO
  last_seen_at: string | null;   // updated_at del al_user (proxy de actividad)
}

export interface AdminCreatePayload {
  email: string;
  password: string;     // mínimo 8 caracteres (validado en backend)
  name?: string | null;
}

export interface AdminPatchPayload {
  name?: string | null;
  is_active?: boolean;
  new_password?: string;   // si se manda, resetea password
}

export async function listAdmins(): Promise<AdminAccount[]> {
  return apiFetch<AdminAccount[]>("/aetherlite/admin/admins");
}

export async function createAdmin(payload: AdminCreatePayload): Promise<AdminAccount> {
  return apiFetch<AdminAccount>("/aetherlite/admin/admins", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function patchAdmin(id: number, payload: AdminPatchPayload): Promise<AdminAccount> {
  return apiFetch<AdminAccount>(`/aetherlite/admin/admins/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function revokeAdmin(id: number): Promise<AdminAccount> {
  return apiFetch<AdminAccount>(`/aetherlite/admin/admins/${id}/revoke`, {
    method: "POST",
    body: "{}",
  });
}

// ============================================================================
// Audit log viewer
// ============================================================================

export interface AuditLogEntry {
  id: number;
  actor_user_id: number | null;
  actor_email: string;
  action: string;                       // ej. "food.create", "admin.create"
  target_type: string | null;           // "food" | "exercise" | "user" | "payment" | "admin"
  target_id: number | null;
  summary: string | null;               // texto humano: 'creó admin "ana@…"'
  payload: any;                         // detalle estructurado del cambio
  ip: string | null;
  user_agent: string | null;
  created_at: string;                   // ISO
}

export interface AuditLogPage {
  entries: AuditLogEntry[];
  total: number;
  page: number;
  page_size: number;
}

export interface AuditLogParams {
  actor_id?: number;
  action?: string;
  target_type?: string;
  q?: string;
  page?: number;
  page_size?: number;
}

export async function listAuditLog(params: AuditLogParams = {}): Promise<AuditLogPage> {
  const qs = new URLSearchParams();
  if (params.actor_id) qs.set("actor_id", String(params.actor_id));
  if (params.action) qs.set("action", params.action);
  if (params.target_type) qs.set("target_type", params.target_type);
  if (params.q) qs.set("q", params.q);
  if (params.page) qs.set("page", String(params.page));
  if (params.page_size) qs.set("page_size", String(params.page_size));
  const q = qs.toString();
  return apiFetch<AuditLogPage>(`/aetherlite/admin/audit-log${q ? `?${q}` : ""}`);
}

// ============================================================================
// Biblioteca de assets premium (íconos + ilustraciones subidos por admin)
// ============================================================================

export type AdminAssetType = "icon" | "illustration";

export interface AdminAsset {
  id: number;
  type: AdminAssetType;
  name: string;
  category: string | null;
  tags: string | null;             // CSV
  url: string;                     // absoluta
  mime: string;                    // image/svg+xml | image/png | etc
  uploaded_by: number | null;
  created_at: string;              // ISO
}

export interface AdminAssetInput {
  type: AdminAssetType;
  name: string;
  category?: string | null;
  tags?: string | null;
  url: string;
  mime: string;
}

/** Listado público (cualquier coach autenticado) para el picker del editor. */
export async function listAssetsForCoach(): Promise<AdminAsset[]> {
  return apiFetch<AdminAsset[]>("/aetherlite/admin/assets/list-for-coach");
}

/** Listado para el panel admin con filtros. */
export async function adminListAssets(params: {
  type?: AdminAssetType;
  category?: string;
  query?: string;
} = {}): Promise<AdminAsset[]> {
  const qs = new URLSearchParams();
  if (params.type) qs.set("type", params.type);
  if (params.category) qs.set("category", params.category);
  if (params.query) qs.set("query", params.query);
  const q = qs.toString();
  return apiFetch<AdminAsset[]>(`/aetherlite/admin/assets${q ? `?${q}` : ""}`);
}

export async function adminCreateAsset(payload: AdminAssetInput): Promise<AdminAsset> {
  return apiFetch<AdminAsset>("/aetherlite/admin/assets", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function adminUpdateAsset(id: number, patch: Partial<AdminAssetInput>): Promise<AdminAsset> {
  return apiFetch<AdminAsset>(`/aetherlite/admin/assets/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export async function adminDeleteAsset(id: number): Promise<void> {
  return apiFetch<void>(`/aetherlite/admin/assets/${id}`, { method: "DELETE" });
}

/** Sube SVG/PNG/WebP al server. Devuelve `{ url, mime, file_size_kb }` para
 *  pasar al POST /assets siguiente (multipart upload separado del create). */
export async function adminUploadAssetFile(
  file: File,
): Promise<{ url: string; mime: string; file_size_kb: number }> {
  const fd = new FormData();
  fd.append("file", file);
  const token = getToken();
  const res = await fetch(`${apiBaseUrl}/aetherlite/admin/assets/upload-file`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Upload failed (${res.status}): ${text}`);
  }
  return res.json();
}
